import _ from 'lodash'
import { Component } from '@wix/thunderbolt-becky-types'
import { ChildrenGroup, DataForMeshCalculation, OverridesData } from '../mesh'
import { MESH_ID_SEPARATOR } from '../meshConstants'
import { MeshResult } from '../../../types/css'
import { getMeshComponents, getMeshData, getStylesAndWedgesAndRotated } from '../meshUtils'

const emptyMeshResults = { stylesMap: {}, wedges: [], rotatedComponents: [] } as MeshResult

const getChildrenWithoutSOSP = (childComponents: Array<Component>): Array<Component> =>
	childComponents.filter((comp) => {
		const hasModes =
			comp.modes && ((comp.modes.overrides && !_.isEmpty(comp.modes.overrides)) || comp.modes.isHiddenByModes)
		return comp.componentType === 'HoverBox' || !hasModes
	})

export const getMeshResultsForMasterPage = (
	compMeshData: DataForMeshCalculation,
	overridesData: OverridesData
): MeshResult => {
	const masterPageChildrenGroups = _.get(compMeshData, 'dataForMasterPage.childrenGroups', []) as Array<ChildrenGroup>

	const getMasterPagePartMeshResult = (
		compId: string,
		childComponents: Array<Component>,
		withSOSPComps: boolean
	): MeshResult => {
		const childrenAfterSOSP = withSOSPComps ? childComponents : getChildrenWithoutSOSP(childComponents)
		const childrenIds = childrenAfterSOSP.map((comp) => comp.id)
		const compStructure = {
			id: compId,
			components: childrenIds,
			componentType: 'SoapContainer',
			layout: {},
			type: 'Container',
			parent: null,
			styleId: '',
			metaData: {},
		} as Component
		const comps = getMeshComponents(compId, {
			..._.keyBy(childrenAfterSOSP, (comp) => comp.id),
			[compStructure.id]: compStructure,
		})

		const nonAbsComps = _.reject(comps, 'absolute')
		const compsWithHeaderOffset = comps.map((comp) => {
			const shouldSubtractHeaderBottom = nonAbsComps.length === 0 && comp.absolute
			const top = shouldSubtractHeaderBottom ? comp.top : comp.top - overridesData.headerBottom

			return { ...comp, top }
		})
		const meshData = getMeshData({ compStructure, compData: {} }, compsWithHeaderOffset, overridesData)

		meshData.styles = withSOSPComps
			? _.mapKeys(meshData.styles, (val, key) => `${key}${MESH_ID_SEPARATOR}page-with-sosp`)
			: _.mapKeys(meshData.styles, (val, key) => `${key}${MESH_ID_SEPARATOR}page-without-sosp`)

		const meshResults = getStylesAndWedgesAndRotated(compId, meshData)

		return childrenIds.length ? meshResults : emptyMeshResults
	}

	const soapBefore = masterPageChildrenGroups.find((group) => group.id === 'soapBeforePagesContainer')
	const soapAfter = masterPageChildrenGroups.find((group) => group.id === 'soapAfterPagesContainer')

	const soapBeforeComps = (soapBefore && soapBefore.components) || []
	const soapAfterComps = (soapAfter && soapAfter.components) || []

	const soapBeforePagesContainerResults = getMasterPagePartMeshResult(
		'soapBeforePagesContainer',
		soapBeforeComps,
		false
	)
	const soapAfterPagesContainerResults = getMasterPagePartMeshResult('soapAfterPagesContainer', soapAfterComps, false)

	const soapBeforePagesContainerResultsWithSOSP = getMasterPagePartMeshResult(
		'soapBeforePagesContainer',
		soapBeforeComps,
		true
	)
	const soapAfterPagesContainerResultsWithSOSP = getMasterPagePartMeshResult(
		'soapAfterPagesContainer',
		soapAfterComps,
		true
	)

	const combinedStyles = {
		...soapBeforePagesContainerResults.stylesMap,
		...soapBeforePagesContainerResultsWithSOSP.stylesMap,
		...soapAfterPagesContainerResults.stylesMap,
		...soapAfterPagesContainerResultsWithSOSP.stylesMap,
	}

	const regularModeRotated = soapBeforePagesContainerResults.rotatedComponents.concat(
		soapAfterPagesContainerResults.rotatedComponents
	)
	const sospModeRotated = soapBeforePagesContainerResultsWithSOSP.rotatedComponents.concat(
		soapAfterPagesContainerResultsWithSOSP.rotatedComponents
	)

	const regularModeWedges = soapBeforePagesContainerResults.wedges.concat(soapAfterPagesContainerResults.wedges)
	const sospModeWedges = soapBeforePagesContainerResultsWithSOSP.wedges.concat(
		soapAfterPagesContainerResultsWithSOSP.wedges
	)

	return {
		stylesMap: combinedStyles,
		rotatedComponents: _.uniq(regularModeRotated.concat(sospModeRotated)),
		wedges: _.uniq(regularModeWedges.concat(sospModeWedges)),
	}
}
